import React, { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { slugToName } from "../lib/categories";
import ProductCard from "../components/ProductCard";
import { Star, ShieldCheck, Truck, RotateCcw, Plus, Minus } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [picks, setPicks] = useState({});

  const { data: p, isLoading } = useQuery({
    queryKey: ["product", id],
    queryFn: async () => (await api.get(`/products/${id}`)).data,
  });

  const { data: reviews = [] } = useQuery({
    queryKey: ["reviews", id],
    queryFn: async () => (await api.get(`/products/${id}/reviews`)).data,
  });

  const { data: related = [] } = useQuery({
    queryKey: ["related", p?.category],
    queryFn: async () => (await api.get("/products", { params: { category: p.category, limit: 8 } })).data,
    enabled: !!p?.category,
  });

  const gallery = useMemo(() => {
    if (!p) return [];
    const arr = (p.images && p.images.length ? p.images : [p.image]).filter(Boolean);
    return arr;
  }, [p]);

  if (isLoading || !p) return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-[#4a4a4a]">Loading…</div>;

  return (
    <div className="bg-white" data-testid="product-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <nav className="text-xs text-[#4a4a4a] mb-6 font-mono uppercase tracking-widest">
          <Link to="/" className="hover:text-[#002fa7]">Home</Link> /{" "}
          <Link to={`/shop/${p.category}`} className="hover:text-[#002fa7]">{slugToName[p.category]}</Link> /{" "}
          <span className="text-[#0a0a0a]">{p.brand}</span>
        </nav>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <div className="aspect-square bg-[#f5f5f5] border border-[#0a0a0a] overflow-hidden">
              <img src={gallery[activeImg] || p.image} alt={p.name} className="w-full h-full object-cover" />
            </div>
            {gallery.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3" data-testid="gallery-thumbs">
                {gallery.map((src, idx) => (
                  <button
                    key={idx}
                    data-testid={`thumb-${idx}`}
                    onClick={() => setActiveImg(idx)}
                    className={`aspect-square overflow-hidden border ${activeImg === idx ? "border-[#002fa7]" : "border-[#e5e5e5]"}`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="lg:col-span-5 space-y-6">
            <div>
              <div className="rh-overline mb-3">{p.brand} · {slugToName[p.category]}</div>
              <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter leading-tight" data-testid="product-title">{p.name}</h1>
              <div className="flex items-center gap-2 mt-3">
                <Star size={14} className="fill-[#0a0a0a] text-[#0a0a0a]" />
                <span className="text-sm font-mono">{p.rating?.toFixed(1)}</span>
                <span className="text-sm text-[#4a4a4a]">({p.reviews} reviews)</span>
              </div>
            </div>

            <div className="border-t border-b border-[#0a0a0a] py-5">
              <div className="font-display font-black text-4xl rh-num" data-testid="product-price">${p.price.toFixed(2)}</div>
              <div className="text-xs text-[#4a4a4a] mt-1">Free shipping on orders over $75 · Tax calculated at checkout</div>
            </div>

            <p className="text-[#4a4a4a] leading-relaxed">{p.description}</p>

            {/* Variants */}
            {p.options && p.options.length > 0 && (
              <div className="space-y-4" data-testid="variant-options">
                {p.options.map((opt) => (
                  <div key={opt.name}>
                    <div className="rh-overline mb-2">{opt.name}</div>
                    <div className="flex flex-wrap gap-2">
                      {opt.values.map((v) => (
                        <button
                          key={v}
                          data-testid={`opt-${opt.name}-${v}`}
                          onClick={() => setPicks((s) => ({ ...s, [opt.name]: v }))}
                          className={`px-4 py-2 border text-sm ${picks[opt.name] === v ? "border-[#002fa7] bg-[#002fa7] text-white" : "border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white"}`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="flex items-center border border-[#0a0a0a]">
                <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-3 hover:bg-[#f5f5f5]"><Minus size={14} /></button>
                <span data-testid="qty-value" className="px-5 font-mono">{qty}</span>
                <button data-testid="qty-inc" onClick={() => setQty((q) => q + 1)} className="px-3 py-3 hover:bg-[#f5f5f5]"><Plus size={14} /></button>
              </div>
              <button
                data-testid="add-to-cart-btn"
                onClick={() => { addItem(p, qty); toast.success(`Added ${qty} × ${p.name.slice(0, 30)}…`); }}
                className="rh-btn-primary flex-1"
              >
                Add to Cart
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 pt-4">
              {[
                { icon: ShieldCheck, t: "Authorized" },
                { icon: Truck, t: "Fast Ship" },
                { icon: RotateCcw, t: "30-Day Return" },
              ].map((b) => (
                <div key={b.t} className="border border-[#e5e5e5] p-3 flex flex-col items-start gap-2">
                  <b.icon size={18} className="text-[#002fa7]" />
                  <span className="text-xs font-semibold">{b.t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Reviews */}
        <section className="mt-20 border-t border-[#0a0a0a] pt-12" data-testid="reviews-section">
          <div className="rh-overline mb-3">Reviews</div>
          <h2 className="font-display font-black text-3xl tracking-tighter mb-8">What customers say ({reviews.length})</h2>

          {user ? (
            <ReviewForm productId={p.product_id} onPosted={() => { qc.invalidateQueries({ queryKey: ["reviews", id] }); qc.invalidateQueries({ queryKey: ["product", id] }); }} />
          ) : (
            <div className="border border-[#e5e5e5] p-5 mb-8 text-sm text-[#4a4a4a]">
              <Link to="/login" className="underline font-semibold text-[#0a0a0a]">Sign in</Link> to write a review.
            </div>
          )}

          <div className="space-y-5">
            {reviews.length === 0 ? (
              <div className="text-[#4a4a4a] text-sm">No reviews yet. Be the first.</div>
            ) : reviews.map((r) => (
              <div key={r.review_id} className="border-b border-[#e5e5e5] pb-5" data-testid={`review-${r.review_id}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex">{[1,2,3,4,5].map((n) => <Star key={n} size={12} className={n <= r.rating ? "fill-[#0a0a0a] text-[#0a0a0a]" : "text-[#e5e5e5]"} />)}</div>
                  <span className="text-sm font-semibold">{r.title}</span>
                </div>
                <p className="text-sm text-[#4a4a4a] mb-2">{r.body}</p>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#4a4a4a]">{r.user_name} · {new Date(r.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Related */}
        {related.length > 1 && (
          <section className="mt-20 border-t border-[#0a0a0a] pt-12">
            <div className="rh-overline mb-3">Related</div>
            <h2 className="font-display font-black text-3xl tracking-tighter mb-8">More in {slugToName[p.category]}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
              {related.filter((r) => r.product_id !== p.product_id).slice(0, 4).map((r) => (
                <ProductCard key={r.product_id} p={r} testIdPrefix="related" />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function ReviewForm({ productId, onPosted }) {
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/products/${productId}/reviews`, { rating, title, body });
      toast.success("Review posted");
      setTitle(""); setBody(""); setRating(5);
      onPosted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="border border-[#0a0a0a] p-5 mb-8 space-y-3" data-testid="review-form">
      <div className="flex items-center gap-3">
        <span className="rh-overline">Your rating</span>
        <div className="flex">
          {[1,2,3,4,5].map((n) => (
            <button type="button" key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
              <Star size={18} className={n <= rating ? "fill-[#0a0a0a] text-[#0a0a0a]" : "text-[#e5e5e5]"} />
            </button>
          ))}
        </div>
      </div>
      <input data-testid="review-title" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Headline" className="w-full border border-[#0a0a0a] px-3 py-2 text-sm" />
      <textarea data-testid="review-body" required value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Share your experience…" className="w-full border border-[#0a0a0a] px-3 py-2 text-sm" />
      <button data-testid="review-submit" type="submit" disabled={busy} className="rh-btn-primary">{busy ? "Posting…" : "Post review"}</button>
    </form>
  );
}
